import React, {memo} from 'react'
import {TModuleListActionButtons, TModuleListCell, TParsedCell} from '../_types'
import moment from 'moment'
import {checkDatesInString, getDateFormat, getMaskedString} from '../../helpers'
import ComponentButton, {getButtonKey} from '../../../components/ComponentButton'
import ComponentAudio from '../../../components/ComponentAudio'
import ComponentTooltip from '../../../components/ComponentTooltip'
import {useSetupContext} from '../../../helpers/SetupContext'
import {KTSVG, toDomainUrl} from '../../../../../_metronic/helpers'
import PortalDropdown from '../../../components/ComponentDropdown/PortalDropdown'
import useCellNavigation from '../hooks/useCellNavigation'
import useCellFilter from '../hooks/useCellFilter'

const PLUG_IMG = toDomainUrl('media/crm/assets/plug.jpg')

function hasPath(v: any): v is {path: string} {
    return v && typeof v === 'object' && typeof v.path === 'string'
}

const ModuleListActionButtons: React.FC<TModuleListActionButtons> = memo(({data}) => {
    if (!Array.isArray(data) || data.length === 0) return null
    const showActionsAsDropdown = data.length >= 4
    return (
        <div className='moduleList_buttonsContainer' onClick={(e) => e.stopPropagation()}>
            {showActionsAsDropdown ? (
                <PortalDropdown buttons={data} />
            ) : (
                data.map((button) => (
                    <ComponentButton
                        key={getButtonKey(button)}
                        {...button}
                        defaultLabel={button.defaultLabel ?? 'icon'}
                        className='moduleList_actionButton'
                    />
                ))
            )}
        </div>
    )
})

// Функция, которая убирает теги и другие ненужные знаки в описании из editor
const delHtml = (value?: string) => {
    if (!value) return ""

    let text = value
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|h[1-6])>/gi, '\n\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim()

    text = text
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim()

    return text
}
// Подбор контрастного цвета текста и фона
const getContrastColor = (hex?: string): string => {
    if (!hex) return '#000'

    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)

    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    return brightness > 145 ? '#000' : '#fff'
}

export const ParsedCell = React.memo<TParsedCell>((props) => {
    const {
        article,
        type,
        row,
        context,
        filterable,
        suffix,
        handleClick,
        setIndividualPage,
        navigate,
        isNewUI,
        is_hex
    } = props
    const data = row[article]

    if (data === null || undefined) {
        return null
    }

    switch (type) {
        case 'email':
            return <a href={`mailto:${data}`}>{data}</a>
        case 'date':
            return (
                <span className={`moduleList_dateCell${data?.color ? ` bg-${data.color}` : ''}`}>
                    {data
                        ? moment(data?.value ?? data).format(getDateFormat(type, context, 'string'))
                        : null}
                </span>
            )
        case 'time':
            return (
                <span className={`moduleList_dateCell${data?.color ? ` bg-${data.color}` : ''}`}>
                    {data
                        ? moment(data?.value ?? data).format(getDateFormat(type, context, 'string'))
                        : null}
                </span>
            )
        case 'datetime':
            return (
                <span className={`moduleList_dateCell${data?.color ? ` bg-${data.color}` : ''}`}>
                    {data
                        ? moment(data?.value ?? data).format(getDateFormat(type, context, 'string'))
                        : null}
                </span>
            )
        case 'list':
            return Array.isArray(data) ? (
                <>
                    {data.map((option, index, array) => {
                        const isLastElement = index === array.length - 1
                        const resolvedKey = JSON.stringify(option) + index

                        return (
                            <span
                                key={resolvedKey}
                                className={`moduleList_cellWrapper${
                                    option?.color ? ` bg-${option.color}` : ''
                                }`}
                            >
                                <span
                                    className={`moduleList_cellData${
                                        filterable ? ' filterable' : ''
                                    }`}
                                    onClick={() => handleClick(option.value)}
                                >{`${option.title?.trim()}${isLastElement ? '' : ', '}`}</span>
                            </span>
                        )
                    })}
                </>
            ) : (
                <span className={`moduleList_cellWrapper${data?.color ? ` bg-${data.color}` : ''}`}>
                    <span
                        className={`moduleList_cellData${filterable ? ' filterable' : ''}`}
                        onClick={() => handleClick(data?.value)}
                    >
                        {data?.title}
                    </span>
                </span>
            )
        case 'buttons':
            return <ModuleListActionButtons data={data} />
        case 'phone':
            return <span>{getMaskedString(data, type, context)}</span>
        case 'price':
            let dataClone = data as
                | number
                | {old_price?: number; new_price?: number; price?: number; icon?: string}
            const currentPriceValue =
                typeof dataClone === 'object' && dataClone
                    ? dataClone?.new_price || dataClone?.price || 0
                    : dataClone
            const previousPriceValue =
                typeof dataClone === 'object' && dataClone ? dataClone?.old_price : null
            const priceIcon = typeof dataClone === 'object' && dataClone ? dataClone?.icon : null
            const resolvedClassName = `moduleList_priceCell ${
                currentPriceValue < 0 ? 'negative' : ''
            } ${currentPriceValue || typeof currentPriceValue === 'number' ? '' : ' hidden'}`
            return (
                <div className={resolvedClassName}>
                    {priceIcon ? (
                        <KTSVG
                            path={`/media/crm/icons/${priceIcon}.svg`}
                            className='moduleList_priceCellIcon'
                        />
                    ) : null}
                    <span className='moduleList_priceCellValue'>
                        {getMaskedString(currentPriceValue, type, context)}
                    </span>
                    {previousPriceValue ? (
                        <span className='moduleList_priceCellPrevious'>
                            {getMaskedString(previousPriceValue, type, context)}
                        </span>
                    ) : null}
                </div>
            )

        case 'float':
        case 'integer':
            return (
                <span
                    className={`moduleList_cellData${filterable ? ' filterable' : ''}`}
                    onClick={() => handleClick(data)}
                >
                    {getMaskedString(data, type, context, suffix)}
                </span>
            )
        case 'image': {
            let imagePath: string | undefined

            if (Array.isArray(data)) {
                const v = data[0]
                if (typeof v === 'string') imagePath = v
                else if (hasPath(v)) imagePath = v.path
            } else if (typeof data === 'string') {
                imagePath = data
            } else if (hasPath(data)) {
                imagePath = data.path
            }

            if (!imagePath) {
                imagePath = PLUG_IMG
            }

            return (
                <div className='moduleList_cellImageContainer'>
                    <img
                        className='moduleList_cellImage'
                        src={imagePath}
                        alt={`${row.id}_${article}`}
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = PLUG_IMG
                        }}
                    />
                </div>
            )
        }
        case 'checkbox':
            const resolvedValue = data === true ? 'Да' : 'Нет'
            return <span>{resolvedValue}</span>
        case 'audio_player': {
            return <ComponentAudio src={data} />
        }
        case 'link_list':
            return Array.isArray(data) ? (
                <>
                    {data.map(
                        (
                            option: {
                                title: string | number
                                value: any
                                href?: string
                                context?: {[key: string]: any}
                            },
                            index,
                            array
                        ) => {
                            const isLastElement = index === array.length - 1
                            return (
                                <span
                                    key={option.value + index}
                                    className={`moduleList_linkCell${
                                        !option?.href ? ' disabled' : ''
                                    }`}
                                    onClick={() =>
                                        option?.href
                                            ? setIndividualPage({
                                                  href: option.href,
                                                  context: option.context ?? {},
                                              })
                                            : null
                                    }
                                >{`${String(option.title)?.trim()}${
                                    isLastElement ? '' : ', '
                                }`}</span>
                            )
                        }
                    )}
                </>
            ) : (
                <span
                    className={`moduleList_linkCell${!data?.href ? ' disabled' : ''}`}
                    onClick={() =>
                        data?.href
                            ? setIndividualPage({href: data.href, context: data.context ?? {}})
                            : null
                    }
                >
                    {data?.title}
                </span>
            )
        case 'link':
            return (
                <span
                    className={`moduleList_linkCell${!data?.href ? ' disabled' : ''}`}
                    onClick={() => (data?.href ? navigate(data.href) : null)}
                >
                    {data?.title}
                </span>
            )
        case 'color_list':
            return Array.isArray(data) ? (
                <>
                    {data.map((option) => {
                        return (
                            <>
                                {isNewUI ? (
                                    <span
                                        className='moduleList_cellWrapper'
                                        onClick={() => handleClick(option.value)}
                                    >
                                        <span
                                            style={{backgroundColor: option.value}}
                                            className='moduleList_colorCell'
                                        />
                                        <span
                                            style={{color: option.value}}
                                            className='moduleList_colorCellTitle'
                                        >
                                            {option.title}
                                        </span>
                                    </span>
                                ) : (
                                    <ComponentTooltip key={option.value} title={option.title}>
                                        <span className='moduleList_cellWrapper'>
                                            <span
                                                style={{backgroundColor: option.value}}
                                                className='moduleList_colorCell'
                                                onClick={() => handleClick(option.value)}
                                            />
                                        </span>
                                    </ComponentTooltip>
                                )}
                            </>
                        )
                    })}
                </>
            ) : (
                <>
                    {isNewUI ? (
                        <span
                            className='moduleList_cellWrapper'
                            onClick={() => handleClick(data.value)}
                        >
                            <span
                                style={{backgroundColor: data.value}}
                                className='moduleList_colorCell'
                            />
                            <span style={{color: data.value}} className='moduleList_colorCellTitle'>
                                {data.title}
                            </span>
                        </span>
                    ) : (
                        <ComponentTooltip key={data.value} title={data.title}>
                            <span className='moduleList_cellWrapper'>
                                <span
                                    style={{backgroundColor: data.value}}
                                    className='moduleList_colorCell'
                                    onClick={() => handleClick(data.value)}
                                />
                            </span>
                        </ComponentTooltip>
                    )}
                </>
            )
        case 'object': {
            if (!Array.isArray(data)) return null

            return (
                <>
                    {data.map((item, index, array) => {
                        const isLast = index === array.length - 1
                        const key = item.id ?? index

                        return (
                            <span key={key} className='moduleList_cellWrapper'>
                                <span
                                    className={`moduleList_cellData${
                                        filterable ? ' filterable' : ''
                                    }`}
                                    onClick={() => handleClick(item.value)}
                                >
                                    {`${item.value}${isLast ? '' : ', '}`}
                                </span>
                            </span>
                        )
                    })}
                </>
            )
        }
        case 'string': {
            // В data может прийти просто строка, либо объект с value и color
            const value = typeof data === 'object' && data?.value !== undefined ? data.value : data
            const color = typeof data === 'object' ? data.color : undefined
            // isHex параметр для отображения цвета у полей, значение которых есть цвет в формате hex
            const isHexColor = is_hex && typeof value === 'string' && value.startsWith('#')
            const hexStyle: React.CSSProperties  | undefined =  isHexColor ? {
                borderRadius: '0.3rem',
                padding: '3px',
                backgroundColor: value,
                color: getContrastColor(value)
            } : undefined
            return (
                <span className={`moduleList_cellWrapper ${color ? ` bg-${color}` : ''}`} style={{borderRadius:"0.475rem"}}>
                  <span
                      className={`moduleList_cellData${filterable ? ' filterable' : ''}`}
                      onClick={() => handleClick(value)}
                      style={hexStyle}
                  >
                {value}
              </span>
            </span>
           )
        }
        case 'editor':
            const preparedValue = delHtml(data?.title ?? data)
            return (
                <span
                    className={`moduleList_cellData${filterable ? ' filterable' : ''}`}
                    onClick={() => handleClick(data?.value ?? data)}
                >
                    {checkDatesInString(preparedValue)}
                </span>
            )
        default:
            return Array.isArray(data) ? (
                <>
                    {data.map((item, index, array) => {
                        const isLastElement = index === array.length - 1
                        const isItemAsObject = item && typeof item === 'object'
                        const resolvedKey =
                            (isItemAsObject ? JSON.stringify(item) : `${item}`) + index

                        return (
                            <span key={resolvedKey} className='moduleList_cellWrapper'>
                                <span
                                    className={`moduleList_cellData${
                                        filterable ? ' filterable' : ''
                                    }`}
                                    onClick={() => handleClick(isItemAsObject ? item.value : item)}
                                >
                                    {`${isItemAsObject ? item.title : item}${
                                        isLastElement ? '' : ', '
                                    }`}
                                </span>
                            </span>
                        )
                    })}
                </>
            ) : (
                <span
                    className={`moduleList_cellData${filterable ? ' filterable' : ''}`}
                    onClick={() => handleClick(data?.value ?? data)}
                >
                    {checkDatesInString(data?.title ?? data)}
                </span>
            )
    }
})

export const ListCell: React.FC<TModuleListCell> = memo((props) => {
    const {article, type, row, page, filterable, suffix, setFilter, setIndividualPage, isNewUI, is_hex} =
        props

    const {context} = useSetupContext()

    const { isDataAsHref, handleCellClick, navigate } = useCellNavigation(
        type,
        row,
        page,
        filterable
    )

    const { handleCellDataClick } = useCellFilter(
        article,
        setFilter,
        filterable
    )

    return (
        <td
            className={`moduleList_cell ${type} ${isDataAsHref ? ' link' : ''}`}
            onClick={handleCellClick}
        >
            <ParsedCell
                article={article}
                type={type}
                row={row}
                context={context}
                filterable={filterable}
                suffix={suffix}
                handleClick={handleCellDataClick}
                navigate={navigate}
                setIndividualPage={setIndividualPage}
                isNewUI={isNewUI}
                is_hex={is_hex}
            />
        </td>
    )
})
